import { useMutation, useQuery } from "@apollo/client";
import { makeStyles, Snackbar, Modal, Fade, Backdrop } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { AccountContext } from "../../../context/account.context";
import { GET_ACTIVE_USER } from "../../../queries/auth.queries";
import {
  ADD_TO_CART,
  GET_ACTIVE_ORDER,
  GET_TOTAL_LINES,
} from "../../../queries/cart.queries";
import {
  h4Medium,
  h6Medium,
  h6Regular,
  pointer,
  getShortMonthAndDay
} from "../../../shared/constants";
import Loader from "../../common/loader/loader.component";
import MuiAlert from "@material-ui/lab/Alert";

import "./account-orders.component.css";
import { useTranslation } from "react-i18next";
import AccountOrderRes from "../account-order-res/account-order-res.component";
import AccountDisplayOrder from "../account-display-order/account-display-order.component";
import OrderTimeLine from "../../common/order-time-line/order-time-line.component";
import { MOVE_STATE } from "../../../queries/checkout.queries";
import { CANCEL_ORDER } from "../../../mutations/cancel-order";
import Currency from '../../../shared/components/currency/Currency';

const Alert = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    marginBottom: 40,

    "@media (max-width:940px)": {
      marginBottom: 16,
      fontSize: 29,
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: 16,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    paddingBottom: 40,
  },
  empty: {
    ...h4Medium,
    opacity: 0.74,
    textAlign: "center",
  },

  headerTitles: {
    // marginRight: 48,
    ...h4Medium,
    opacity: 0.64,
    ...pointer,
  },

  tableHeaders: {
    ...h6Regular,
    opacity: 0.4,
  },

  tableTitles: {
    ...h6Regular,
    opacity: 0.4,
  },

  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

const AccountOrders = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [currentTab, setCurrentTab] = useState(0);
  const [itemToAdd, setItemToAdd] = useState({
    id: null,
    qty: 0,
  });

  const [isSnackOpen, setIsSnackOpen] = useState(false);
  const [isOrderSnackOpen, setIsOrderSnackOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [orderCode, setOrderCode] = useState(null);
  const [index, setIndex] = useState(null);
  const [orderIdToCancel, setOrderIdToCancel] = useState(null);

  const { accountState, setAccountState } = useContext(AccountContext);

  const { loading, error, data } = useQuery(GET_ACTIVE_USER);

  const [addToCart, { loading: addToCardLoader }] = useMutation(ADD_TO_CART, {
    variables: {
      variantId: itemToAdd.id,
      qty: itemToAdd.qty,
    },
    refetchQueries: [{ query: GET_TOTAL_LINES }, { query: GET_ACTIVE_ORDER }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsSnackOpen(true);
    },
  });

  const [cancelOrder, { loading: cancelOrderLoader }] = useMutation(CANCEL_ORDER, {
    variables: { id: orderIdToCancel },
    refetchQueries: [{ query: GET_TOTAL_LINES }, { query: GET_ACTIVE_ORDER }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.cancelOpenOrder?.__typename && data?.cancelOpenOrder?.__typename === "Order") {
        setIsOrderSnackOpen(true);
        setTimeout(() => {
          window.location.reload();
        }, 1000)
      } else {
        alert('Something went wrong!')
      }
    },
  });

  useEffect(() => {
    if (!orderIdToCancel) return;

    cancelOrder();
  }, [orderIdToCancel]);


  const onCancelOrder = (index) => {
    setOrderIdToCancel(index)
  };

  const { data: activeOrder } = useQuery(GET_ACTIVE_ORDER);

  const [moveStateToAddingItems] = useMutation(MOVE_STATE, {
    variables: {
      state: "AddingItems",
    },
    refetchQueries: [{ query: GET_ACTIVE_USER }],
    onCompleted: async () => await addToCart(),
  });

  useEffect(() => {
    if (data) {
      setAccountState({
        ...accountState,
        isUser: !!data.activeCustomer,
        user: data.activeCustomer,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  React.useEffect(() => {
    if (!itemToAdd.id || itemToAdd.qty === 0) return;
    addItemToOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemToAdd]);

  const onAddLineToBasket = async (line) => {
    setItemToAdd({ id: line?.productVariant?.id, qty: line?.quantity });
  };

  const addItemToOrder = async () => {
    if (
      !activeOrder?.activeOrder ||
      activeOrder?.activeOrder?.state === "AddingItems"
    ) {
      await addToCart();
    }
    if (activeOrder?.activeOrder?.state === "ArrangingPayment") {
      await moveStateToAddingItems();
    }
  };

  if (loading) return <Loader color={"#B73232"} size={70} />;

  if (error) {
    console.log(error);
  }

  const onAddToBasket = async (item) => {
    for (const line of item.lines) {
      await addToCart({
        variables: {
          variantId: line?.productVariant?.id,
          qty: line?.quantity,
        }
      });
    }
  };

  const onCloseSnack = (bool) => {
    setIsSnackOpen(bool);
  };

  const onCloseOrderSnack = (bool) => {
    setIsOrderSnackOpen(bool);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const tableTitles = [
    `${t("account.Number")}`,
    `${t("account.deliveryDateAndTime")}`,
    `${t("account.Amount")}`,
    `${t("account.Status")}`,
  ];
  const orders = data?.activeCustomer?.orders?.items;
  let arrayForSort = orders?.length ? [...orders] : [];

  arrayForSort = arrayForSort.reverse();
  arrayForSort = arrayForSort.filter((item) => item.state !== "AddingItems");

  const onOrderRowClicked = (e, target) => {
    e.preventDefault();
    if (target === "row") {
      setOpen(true);
      return;
    }
  }
  return (
    <React.Fragment>
      <div className={classes.container}>
        <span className={`h2 ${classes.title}`}>{t("account.Orders")}</span>

        <div className="account-order-res">
          <AccountOrderRes orders={arrayForSort} onAddToBasket={onAddToBasket}
            addToCardLoader={addToCardLoader} index={index} setIndex={setIndex}
            onCancelOrder={onCancelOrder} cancelOrderLoader={cancelOrderLoader} />
        </div>
        <div className="account-order-reg">
          <div className={classes.content}>
            <div
              className={classes.header}
              style={{ width: "90%", margin: "0 auto", paddingTop: 40 }}
            >
              {[
                `${t("account.Confirmed")}`,
                // `${t("account.Confirmed")} ${arrayForSort?.length}`,
                // `${t("account.Recurring Delivery")} 0`,
              ].map((title, i) => (
                <span
                  key={i}
                  className={classes.headerTitles}
                  style={{ opacity: currentTab === i ? 1 : 0.64 }}
                  onClick={() => {
                    setCurrentTab(i);
                  }}
                >
                  {title} {arrayForSort?.length}
                </span>
              ))}
            </div>

            {orders?.length ? (
              <table style={{ width: "90%", margin: "0 auto" }}>
                <tr>
                  {tableTitles.map((h, i) => (
                    <td key={i} className={classes.tableHeaders}>
                      {h}
                    </td>
                  ))}
                  <td></td>
                </tr>
                {arrayForSort.map((o, i) => {
                  return (
                    <tr key={i}
                      className="pointer"
                      onClick={async (e) => {
                        setOrderCode(o.code);
                      }}>
                      <td
                        style={{
                          ...h6Regular,
                          opacity: 0.64,
                          ...pointer,
                        }}
                        onClick={async (e) => {
                          onOrderRowClicked(e, "row");
                        }}
                      >
                        {o.code}
                      </td>
                      <td style={{ ...h6Medium }} onClick={async (e) => {
                        onOrderRowClicked(e, "row");
                      }}>
                        {o?.deliveryTime ? getShortMonthAndDay(o?.deliveryTime) : null}
                        {o?.timeWindow?.description}
                      </td>
                      <td style={{ ...h6Medium }} onClick={async (e) => {
                        onOrderRowClicked(e, "row");
                      }}>
                        <Currency /> {(o.totalWithTax / 100).toFixed(2)}
                      </td>
                      <td style={{ ...h6Medium }} onClick={async (e) => {
                        onOrderRowClicked(e, "row");
                      }}>
                        <OrderTimeLine state={o?.state} />
                      </td>
                      {o?.state !== "Cancelled" ? <td
                        style={{
                          color: "#80BB34",
                          ...h6Medium,
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, height: 50 }}>
                          {addToCardLoader && i === index ?
                            <Loader color={"#B73232"} size="15" />
                            : <span
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                setIndex(i);
                                onAddToBasket(o);
                              }}>
                              {t("account.Add to basket")}
                            </span>}
                        </div>
                      </td> : null}
                    </tr>
                  );
                })}
              </table>
            ) : (
              <span className={classes.empty}>
                {t("account.No orders found")}
              </span>
            )}
          </div>
        </div>

        <Snackbar
          open={isSnackOpen}
          autoHideDuration={1000}
          onClose={() => onCloseSnack(false)}
          anchorOrigin={{
            vertical: "top",
            horizontal: `${t("css.snackBarPosition")}`,
          }}
        >
          <Alert onClose={() => onCloseSnack(false)} severity="success">
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>
                {t("itemPage.ItemEditMessage", {
                  action: `${t("itemPage.added")}`,
                })}
              </span>
            </div>
          </Alert>
        </Snackbar>

        <Snackbar
          open={isOrderSnackOpen}
          autoHideDuration={1000}
          onClose={() => onCloseOrderSnack(false)}
          anchorOrigin={{
            vertical: "top",
            horizontal: `${t("css.snackBarPosition")}`,
          }}
        >
          <Alert onClose={() => onCloseOrderSnack(false)} severity="success">
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>
                {t("itemPage.ItemEditMessage", {
                  action: `${t("itemPage.cancelled")}`,
                })}
              </span>
            </div>
          </Alert>
        </Snackbar>
      </div>

      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <AccountDisplayOrder
            code={orderCode}
            handleClose={handleClose}
            onAddLineToBasket={onAddLineToBasket}
            onAddToBasket={onAddToBasket}
            addToCardLoader={addToCardLoader}
          />
        </Fade>
      </Modal>
    </React.Fragment>
  );
};

export default AccountOrders;
